// Where a figure came from, beside the figure.
//
// Nothing stores a charge any more: a period's figure is its term lines
// interpreted over that period's facts, and the interpretation says which arm
// supplied each figure. So the same column holds figures of five different
// kinds, and a reader who cannot tell them apart will treat a figure that is
// still moving as one that is settled:
//
//   CONTRACT       the paper states it. The ordinary case.
//   DEAL_FACT      somebody recorded it, or a feed did.
//   PROVIDER_COST  the provider's own figure for the day.
//   RATE_CARD      our card priced it, because no provider figure covered it.
//   NONE           no figure at all. The empty slot each screen already draws.
//
// THE ORDINARY CASE CARRIES NO MARK, the same rule `SplitMark` keeps: almost
// every figure comes off the contract, and a mark on the ordinary case is a
// mark that stops meaning anything. Only a figure the facts supplied, an open
// period and a withheld figure change how a reader should treat the number.
//
// GROWING IS DRAWN AND SAID. The sparkline is the claim "this is still adding
// up" in one glance; the words "month to date" are the same claim for a reader
// who cannot see it, and they appear whether or not there is a line to draw —
// the rollup answers per deployment, and a partner-wide charge is bound to
// none, so the picture is the part that is allowed to be missing.
//
// AN UNPRICED COUNT IS A FIGURE, NOT A GAP. A selector that refuses a call
// reports how many it refused, so "we do not bill auto-transfers" reads as a
// count rather than as an absence. `unpricedNote` states it in one clause.
//
// Tokens only, and no colour of its own: this rides inside table cells that
// already set their own tone, and a mark that repainted them would compete with
// the figure it is annotating.

import type { ReactNode } from "react";
import {
  UnpricedReason,
  type DealStatementUnpriced,
  type DerivedFigureIssue,
  type FigureSource,
} from "@crm/shared";
import styles from "./FigureProvenance.module.css";
import { pointX, valueY } from "./chart/scale.js";
import { figureSourceLabel, unpricedReasonLabel } from "../format/labels.js";
import { assertNever } from "../lib/assertNever.js";
import { formatCount } from "../format/money.js";

/** One day of a month, as the rollup grouped it. */
export interface GrowthDay {
  readonly day: string;
  readonly costMinor: number;
}

export interface FigureProvenanceProps {
  readonly source: FigureSource;
  /** True while the period can still grow; null when the line is not fact-backed. */
  readonly periodOpen: boolean | null;
  readonly issue: DerivedFigureIssue | null;
  /** What the line could not price. Absent draws nothing. */
  readonly unpriced?: DealStatementUnpriced;
  /** The month so far, day by day. Absent or empty draws no picture. */
  readonly growth?: readonly GrowthDay[];
  /** How the sparkline's ends are spelled out. Defaults to bare minor units. */
  readonly formatValue?: (minor: number) => string;
}

const PLOT_WIDTH = 64;
const PLOT_HEIGHT = 16;

/**
 * Why a period can carry no figure at all.
 *
 * None of these is a person to chase, which is why they are said rather than
 * left as an empty slot with a button on it: a screen that only drew the slot
 * would send somebody after a partner who owes us nothing.
 */
function issueReason(issue: DerivedFigureIssue): string {
  switch (issue) {
    case "currency_mismatch":
      return "the facts priced this period in another currency, and nothing is converted here";
    case "fx_rate_unavailable":
      return "no rate covers the day these facts fall on, and a rate is a fact we do not have";
    case "entitlement_unmetered":
      return "a pool covers this line, and no quantity rule says what it consumes";
    case "entitlement_unit_mismatch":
      // Withheld rather than billed at the full rate: a person looking at a
      // pool believes it is reducing this line, and a bill that ignored it
      // would be the figure they least expect.
      return "a pool lists this line and holds another unit, so neither can reduce the other";
    case "entitlement_cycle_conflict":
      return "two pools cover this line over cycles of different lengths, so what it used has two answers";
    case "milestone_price_unsupported":
      return "a milestone is a share of a flat amount, and this line prices units or reads the facts' own money";
    case "issued_snapshot_ambiguous":
      // Two documents, one clause, one period. Only one of them went out, and
      // a screen that picked either would show a figure nobody can check.
      return "two issued documents carry this line for this period, and only one of them can be the one that went out";
    case "entitlement_overdrawn":
      // Withheld rather than billed: an opening balance is never negative, and
      // the alternative is to charge a customer for units nobody used.
      return "stated adjustments drove the prepaid balance below zero, so every covered figure is withheld";
    case "rule_missing":
      return "nobody has written this line's quantity rule yet";
    default:
      return assertNever(issue, "derived figure issue");
  }
}

/**
 * The counts a line could not price, or null when it priced everything.
 *
 * The reasons come from the CONTRACT's own list rather than from a copy of it
 * here: `unpriced` carries one count per reason, and a reason added to the
 * contract has to appear on the page rather than be silently dropped by a
 * renderer that never heard of it.
 */
export function unpricedNote(unpriced: DealStatementUnpriced): string | null {
  const parts: string[] = [];
  for (const reason of UnpricedReason.options) {
    const count = unpriced[reason];
    if (count > 0) parts.push(`${formatCount(count)} ${unpricedReasonLabel(reason)}`);
  }
  return parts.length === 0 ? null : `not billed: ${parts.join(" · ")}`;
}

/**
 * Whether the facts, rather than the paper, supplied this figure.
 *
 * Closed over the contract's five sources, so a source added to it is a
 * compile error here rather than a figure that quietly loses its mark.
 */
function fromFacts(source: FigureSource): boolean {
  switch (source) {
    case "deal_fact":
    case "provider_cost":
    case "rate_card":
      return true;
    case "contract":
    case "none":
      return false;
    default:
      return assertNever(source, "figure source");
  }
}

export function FigureProvenance({
  source,
  periodOpen,
  issue,
  unpriced,
  growth,
  formatValue,
}: FigureProvenanceProps): ReactNode {
  if (issue !== null) {
    return <span className={styles.withheld}>{issueReason(issue)}</span>;
  }
  const counts = unpriced === undefined ? null : unpricedNote(unpriced);
  if (!fromFacts(source)) {
    return counts === null ? null : <span className={styles.withheld}>{counts}</span>;
  }
  return (
    <span className={styles.mark}>
      <span className={styles.provenance}>{`from ${figureSourceLabel(source)}`}</span>
      {periodOpen === true && (
        <span className={styles.growing}>
          <Sparkline days={growth ?? []} formatValue={formatValue} />
          <span className={styles.growingWord}>month to date</span>
        </span>
      )}
      {counts !== null && <span className={styles.withheld}>{counts}</span>}
    </span>
  );
}

/**
 * The month so far, as one rising line.
 *
 * CUMULATIVE, not per-day: the claim is "this is still adding up", and a
 * per-day line falls as often as it rises, which reads as a figure that moves
 * both ways. A month's running total only ever rises, so the shape IS the
 * argument.
 *
 * The picture carries no axis and no ticks. It sits beside the figure it
 * annotates, that figure is the value at the right-hand end, and a 64-pixel
 * plot with a ruling on it would be a chart nobody can read pretending to be
 * one somebody can.
 */
function Sparkline({
  days,
  formatValue,
}: {
  days: readonly GrowthDay[];
  formatValue?: (minor: number) => string;
}): ReactNode {
  if (days.length === 0) return null;

  const ordered = [...days].sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
  let running = 0;
  const totals = ordered.map((entry) => {
    running += entry.costMinor;
    return running;
  });
  const top = totals[totals.length - 1] ?? 0;
  const say = formatValue ?? ((minor: number) => String(minor));
  const points = totals
    .map(
      (total, index) =>
        `${pointX(index, totals.length, PLOT_WIDTH)},${valueY(total, top, PLOT_HEIGHT)}`,
    )
    .join(" ");

  return (
    <svg
      className={styles.spark}
      viewBox={`0 0 ${PLOT_WIDTH} ${PLOT_HEIGHT}`}
      role="img"
      // The two ends of the run and how many days are in it — the whole of what
      // the line says, for a reader who is not looking at it.
      aria-label={`Month to date, growing over ${formatCount(totals.length)} days to ${say(top)}.`}
    >
      <polyline className={styles.sparkLine} points={points} />
    </svg>
  );
}
